import { ITranslationsCollection } from '@estee/elc-service';
import { observable } from 'mobx';
import {
    INarvarOrderInfo,
    IOrderApiResponse,
    IOrderNarvarItem,
    IOrderNarvarShipment,
    IShipment
} from '../../../api/OrderTrackingApiSdk';
import {
    DEFAULT_DATETIME_FORMAT,
    DEFAULT_DATETIME_STAMP_FORMAT,
    DEFAULT_DATE_FORMAT,
    DEFAULT_ITEM_TO_SLICE
} from '../../utils/Constants';
import { formatDate, formatOrderDate, getUpdatedOrder } from '../../utils/DateUtils';
import { IOrderTrackingConfig, IShipmentOrder, OrderNumberType } from './Config';
import { OrderStatus } from './OrderStatus';
import { ShippingInfo } from './ShippingInfo';
import { ShipmentItem } from './ShipmentItem';

export type PickupStoreInfo = {
    pickup_store_name: string;
    pickup_chain_code: string;
    pickup_chain_name: string;
    pickup_tracking_url: string;
    pickup_tracking_number: string;
};

export class OrderInfo {
    @observable public orderNumber: string = '';
    @observable public orderDate: string;
    @observable public deliveryMethodCode: string = '';
    @observable public deliveryMethodName: string = '';
    @observable public orderStatusCode: string = '';
    @observable public orderDateFormattedString: string;

    @observable public shippingInfo?: ShippingInfo;
    @observable public orderStatus?: OrderStatus;
    @observable public shipmentOrders?: IShipmentOrder[];
    @observable public orderItems: IOrderNarvarItem[];
    @observable public pickupStoreInfo?: PickupStoreInfo;

    @observable public deliveredDate: string = '';
    @observable public finalSale: boolean;

    private checkPartialMultipleShipment = (shipments: IShipment[]) => {
        const ongoingShipmentsLength = shipments.filter((x) => !x.is_complete).length;
        const completedShipmentsLength = shipments.filter((x) => x.is_complete).length;

        return (
            (ongoingShipmentsLength >= 1 && completedShipmentsLength >= 1) ||
            ongoingShipmentsLength > 1
        );
    };

    private checkPartialShipment = (
        isVerticalTMOProgressBarDisplay: boolean,
        currentOrderStatusCode: string,
        partialOrderStatusCode: string,
        shipmentList?: IOrderNarvarShipment[]
    ) => {
        return (
            isVerticalTMOProgressBarDisplay &&
            ((shipmentList && shipmentList.length > 1) ||
                partialOrderStatusCode === currentOrderStatusCode)
        );
    };

    private setNarvarData = (
        data: IOrderApiResponse,
        orderTrackingConfig: IOrderTrackingConfig,
        orderInfo: OrderInfo,
        translations: ITranslationsCollection,
        defaultCarrierCode?: string
    ) => {
        const {
            order_status,
            narvar_data,
            carrier,
            order_date,
            shipped_date,
            tracking_number,
            narvar_ship_method,
            trans_id,
            trans_order_id
        } = data;
        const narvarData: INarvarOrderInfo | undefined =
            typeof narvar_data === 'string' ? undefined : narvar_data?.order_info;

        const {
            isVerticalTMOProgressBarDisplay,
            SetReturnInitiatedStatusCode,
            dateTimeStampFormatPattern,
            partialShipmentStatusCode,
            datetimeFormatPattern = DEFAULT_DATETIME_FORMAT,
            orderStatusCodeMap,
            orderStatusLabelMap,
            breadCrumbDelimiter,
            dateFormatPattern,
            carrierCodes
        } = orderTrackingConfig;
        let narvarCodeStatus;
        let isNarvarReturn = false;
        let isNarvarPartialShipment = false;
        let narvarStatusTimestamp;
        let narvarEddDate;

        if (narvarData?.returns) {
            isNarvarReturn = true;
            narvarData.returns.forEach((item) => {
                const status = item.shipments.find((i) => i.status)?.status_code;
                const timestamp = item.shipments.find((i) => i.status)?.status_timestamp;
                narvarCodeStatus = status ? status : SetReturnInitiatedStatusCode;
                narvarStatusTimestamp = timestamp
                    ? formatOrderDate(timestamp, dateTimeStampFormatPattern)
                    : '';
            });
        } else if (narvarData?.shipments) {
            isNarvarReturn = false;
            isNarvarPartialShipment = orderInfo.checkPartialShipment(
                isVerticalTMOProgressBarDisplay,
                partialShipmentStatusCode,
                `${order_status}`,
                narvarData.shipments
            );
            const filterData = getUpdatedOrder(narvarData.shipments);
            if (filterData) {
                if (filterData.delivery?.guaranteed_date) {
                    narvarEddDate = formatOrderDate(
                        filterData.delivery.guaranteed_date,
                        datetimeFormatPattern
                    );
                } else {
                    narvarEddDate = filterData.delivery?.estimated_date
                        ? formatOrderDate(filterData.delivery.estimated_date, datetimeFormatPattern)
                        : '';
                }
                narvarCodeStatus = filterData?.status_code ? filterData.status_code : undefined;
                narvarStatusTimestamp = filterData?.status_timestamp
                    ? formatOrderDate(filterData.status_timestamp, dateTimeStampFormatPattern)
                    : '';
            }
        }
        const statusCode = narvarCodeStatus || `${order_status}`;
        orderInfo.orderStatusCode = statusCode;
        orderInfo.orderStatus = new OrderStatus(
            statusCode,
            orderStatusCodeMap,
            translations,
            isVerticalTMOProgressBarDisplay,
            narvarEddDate || '',
            orderStatusLabelMap,
            breadCrumbDelimiter,
            isNarvarReturn,
            narvarStatusTimestamp || '',
            isNarvarPartialShipment
        );
        orderInfo.orderDateFormattedString = formatDate(order_date, dateFormatPattern);
        if (orderInfo.orderStatus.isShipped) {
            const shippingInfoData = {
                carrier,
                order_date,
                ship_date: shipped_date,
                tracking_number,
                service: narvar_ship_method || '',
                trans_id,
                trans_order_id
            };
            orderInfo.shippingInfo = new ShippingInfo(
                shippingInfoData,
                { dateFormatPattern, carrierCodes },
                defaultCarrierCode
            );
        }
    };

    private setShipments = (
        data: IOrderApiResponse,
        orderTrackingConfig: IOrderTrackingConfig,
        translations: ITranslationsCollection,
        defaultCarrierCode: string | undefined,
        orderInfo: OrderInfo
    ) => {
        const {
            order_date,
            narvar_ship_method,
            trans_id,
            trans_order_id,
            shipments,
            carrier,
            tracking_number
        } = data;
        const {
            dateFormatPattern = DEFAULT_DATE_FORMAT,
            carrierCodes,
            orderStatusCodeMap,
            isVerticalTMOProgressBarDisplay,
            datetimeFormatPattern = DEFAULT_DATETIME_FORMAT,
            orderStatusLabelMap,
            breadCrumbDelimiter,
            dateTimeStampFormatPattern = DEFAULT_DATETIME_STAMP_FORMAT
        } = orderTrackingConfig;
        if (shipments) {
            const isPartialShipment = orderInfo.checkPartialMultipleShipment(shipments);
            orderInfo.shipmentOrders = shipments?.map((item) => {
                const formatStatusTimestamp =
                    formatOrderDate(item.last_updated_date, dateTimeStampFormatPattern) || '';

                const formattedDeliveredDate = item.delivered_date
                    ? formatOrderDate(item.delivered_date, datetimeFormatPattern)
                    : formatOrderDate(item.estimated_delivery_date, datetimeFormatPattern);
                const shipmentData = new OrderStatus(
                    `${item.shipment_status}`,
                    orderStatusCodeMap,
                    translations,
                    isVerticalTMOProgressBarDisplay,
                    formattedDeliveredDate || '',
                    orderStatusLabelMap,
                    breadCrumbDelimiter,
                    item.is_return,
                    formatStatusTimestamp,
                    isPartialShipment,
                    item.items
                );

                const partialShippedData = {
                    carrier: item.carrier ?? carrier,
                    order_date,
                    ship_date: item.shipped_date,
                    tracking_number: item.shipment_tracking_number ?? tracking_number,
                    service: narvar_ship_method || '',
                    trans_id,
                    trans_order_id
                };
                const shipInfo = new ShippingInfo(
                    partialShippedData,
                    { dateFormatPattern, carrierCodes },
                    defaultCarrierCode,
                    item.is_return,
                    item.return_tracking_url
                );
                const progressOrder = {
                    inProgressOrder: item.is_complete
                };

                const orderItems: ShipmentItem[] = item.items.map(ShipmentItem.fromJSON);
                const hasMoreSlideHeaderOrderItems = orderItems.length > 3;
                const slideHeaderOrderItems = hasMoreSlideHeaderOrderItems
                    ? orderItems.slice(0, 2)
                    : orderItems;
                const itemToSlice = hasMoreSlideHeaderOrderItems
                    ? `${orderItems.length - DEFAULT_ITEM_TO_SLICE}`
                    : '';

                return {
                    ...shipmentData,
                    ...shipInfo,
                    ...progressOrder,
                    orderItems,
                    hasMoreSlideHeaderOrderItems,
                    slideHeaderOrderItems,
                    itemToSlice
                };
            });
        }
    };

    public static fromJSON(
        data: IOrderApiResponse,
        orderTrackingConfig: IOrderTrackingConfig,
        translations: ITranslationsCollection
    ): OrderInfo {
        const orderInfo = new OrderInfo();
        const {
            trans_order_id,
            order_date,
            delivery_method,
            delivered_date,
            order_status,
            trans_id,
            carrier,
            shipped_date,
            tracking_number,
            narvar_ship_method,
            narvar_data,
            shipments,
            pickup_store_data,
            final_sale
        } = data;

        const orderIdOptions = {
            [OrderNumberType.TOID]: trans_order_id,
            [OrderNumberType.TID]: trans_id
        };
        const {
            deliveryMethods,
            isVerticalTMOProgressBarDisplay,
            partialShipmentStatusCode,
            orderStatusLabelMap,
            breadCrumbDelimiter,
            orderStatusCodeMap,
            dateFormatPattern,
            carrierCodes,
            orderNumberType
        } = orderTrackingConfig;
        const orderId = orderIdOptions[orderNumberType];
        const narvarData = typeof narvar_data === 'string' ? undefined : narvar_data?.order_info;

        orderInfo.orderItems = narvarData?.order_items ?? [];

        let defaultCarrierCode: string | undefined;
        let isPartialShipment = false;

        if (orderId) {
            orderInfo.orderNumber = `${orderId}`;
        }
        orderInfo.orderDate = order_date;
        orderInfo.deliveredDate = delivered_date;
        if (delivery_method) {
            const deliveryMethodCode = `${delivery_method}`;
            orderInfo.deliveryMethodCode = deliveryMethodCode;
            const deliveryMethod = deliveryMethods.find(
                (item) => item.methodCode === deliveryMethodCode
            );
            if (deliveryMethod) {
                defaultCarrierCode = deliveryMethod.defaultCarrierCode;
                orderInfo.deliveryMethodName = deliveryMethod.methodName || '';
            }
        }
        isPartialShipment = shipments
            ? orderInfo.checkPartialMultipleShipment(shipments)
            : orderInfo.checkPartialShipment(
                  isVerticalTMOProgressBarDisplay,
                  partialShipmentStatusCode,
                  `${order_status}`
              );
        if (tracking_number && narvarData && isVerticalTMOProgressBarDisplay && !shipments) {
            orderInfo.setNarvarData(
                data,
                orderTrackingConfig,
                orderInfo,
                translations,
                defaultCarrierCode
            );
        } else {
            const formattedDeliveredDate = '';
            const formatStatusTimestamp = '';
            const statusCode = `${order_status}`;
            orderInfo.orderStatusCode = statusCode;
            orderInfo.orderStatus = new OrderStatus(
                statusCode,
                orderStatusCodeMap,
                translations,
                isVerticalTMOProgressBarDisplay,
                formattedDeliveredDate,
                orderStatusLabelMap,
                breadCrumbDelimiter,
                false,
                formatStatusTimestamp,
                isPartialShipment
            );
            orderInfo.orderDateFormattedString = formatDate(order_date, dateFormatPattern);

            if (orderInfo.orderStatus.isShipped) {
                const shippingInfoData = {
                    carrier,
                    order_date,
                    ship_date: shipped_date,
                    tracking_number,
                    service: narvar_ship_method || '',
                    trans_id,
                    trans_order_id
                };
                orderInfo.shippingInfo = new ShippingInfo(
                    shippingInfoData,
                    { dateFormatPattern, carrierCodes },
                    defaultCarrierCode
                );
            }
        }
        //For the shipment keys to show the partial shipment or multi shipment orders
        orderInfo.setShipments(
            data,
            orderTrackingConfig,
            translations,
            defaultCarrierCode,
            orderInfo
        );

        if (pickup_store_data) {
            orderInfo.pickupStoreInfo = pickup_store_data;
        }

        orderInfo.finalSale = final_sale === '1';

        return orderInfo;
    }
}
